import { useEffect, useRef, useState } from "react";

interface DropdownProps {
  options: any[];
  onChange: (event: { target: { name: string; value: string } }) => void;
  error?: string;
  value?: any;
  name: string;
  label?: string;
  hero?: boolean;
  payments?: boolean;
  disabled?: boolean;
}

const SearchableDropdown = ({
  options,
  onChange,
  error,
  value,
  name,
  hero,
  label,
  payments,
  disabled,
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ChevronDown = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#868C98"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );

  const ChevronUp = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#868C98"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus the input when opening
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleSelect = (option: any) => {
    setIsOpen(false);
    setSearchTerm("");
    onChange({ target: { name, value: option } });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);

    // Filter options based on search term
    const filtered = options.filter((option) =>
      option.toString().toLowerCase().includes(term.toLowerCase()),
    );
    setFilteredOptions(filtered);
  };

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
        setFilteredOptions(options);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [options]);

  return (
    <div className="relative w-full" ref={selectRef}>
      <div className="relative z-10">
        {isOpen ? (
          <div
            className={`flex items-center w-full h-12 px-4 py-2 border rounded-lg shadow-sm ${
              error
                ? "border-red-500"
                : hero || payments
                  ? "border-bnp-cyan"
                  : "border-soft-200"
            } bg-white`}
          >
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              className="w-full bg-transparent outline-none text-sm"
              placeholder="Type to search..."
            />
            <button type="button" onClick={handleToggle}>
              <ChevronUp />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleToggle}
            className={`flex justify-between items-center w-full h-12 px-4 py-2 text-left border rounded-lg shadow-sm ${
              error
                ? "border-red-500"
                : hero
                  ? "border-white bg-transparent"
                  : "border-soft-200 bg-white"
            } transition-colors`}
          >
            <div className="flex flex-col items-start">
              <span
                className={`mt-1 max-w-[160px] truncate text-sm ${
                  hero && !value
                    ? "text-white"
                    : hero && value
                      ? "text-white"
                      : !value && !hero
                        ? "text-grey-4"
                        : "text-black"
                }`}
              >
                {value || "Select option"}
              </span>
            </div>
            <ChevronDown />
          </button>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute w-full">
          <ul className="relative z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={index}
                  onClick={() => handleSelect(option)}
                  className={`text-sm px-4 py-2 cursor-pointer text-grey-7 ${
                    option === value
                      ? "bg-bnp-teal font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {option}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No matches found</li>
            )}
          </ul>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default SearchableDropdown;
